'use client';

import { Button } from '@v2/ui/Button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@v2/ui/Drawer';
import { PhoneCall01 } from '@v2/ui/Icon/Communication';
import { Copy06 } from '@v2/ui/Icon/General';
import { Popover, PopoverContent, PopoverTrigger } from '@v2/ui/Popover';
import { cn } from '@v2/utils/cn';
import { useEffect, useState } from 'react';

export const SUPPORT_PHONE_DISPLAY = '1 (888) 908-1042';
export const SUPPORT_PHONE_TEL = '1-888-908-1042';

export function NeedHelpButton({ compact = false }: { compact?: boolean }) {
  const trigger = (
    <button
      type="button"
      aria-label="Need help?"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg text-gray-800',
        compact
          ? 'size-10'
          : 'h-9 gap-2 px-3.5 py-2 text-sm font-semibold leading-5'
      )}
    >
      <PhoneCall01 size="20" className="size-5 shrink-0" />
      {compact ? null : (
        <span className="hidden whitespace-nowrap lg:inline">Need Help?</span>
      )}
    </button>
  );

  if (compact) {
    return (
      <Drawer shouldScaleBackground={false}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="rounded-t-[24px] border-0 bg-white pb-[env(safe-area-inset-bottom)] [&>div:first-child]:mt-3 [&>div:first-child]:h-1 [&>div:first-child]:w-10 [&>div:first-child]:rounded-2xl [&>div:first-child]:bg-gray-300 [&>div:first-child]:opacity-70">
          <DrawerHeader className="p-4 text-center sm:text-center">
            <DrawerTitle className="text-2xl font-bold leading-8 tracking-normal text-gray-800">
              Need Help?
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              Call us at {SUPPORT_PHONE_DISPLAY}, Monday through Friday, 9am to
              5pm EST.
            </DrawerDescription>
          </DrawerHeader>
          <NeedHelpDetails className="px-4 pb-4 pt-2" />
          <NeedHelpActions className="border-t border-gray-200 px-4 pb-4 pt-4" />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-[293px] overflow-hidden rounded-lg border-gray-200 bg-white p-0 shadow-shadowLg"
      >
        <NeedHelpDetails className="p-4" />
        <NeedHelpActions className="px-4 pb-4" />
      </PopoverContent>
    </Popover>
  );
}

function NeedHelpDetails({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p className="text-sm font-semibold leading-6 text-gray-500">Call us</p>
      <p className="truncate text-xl font-bold leading-[30px] text-gray-800">
        {SUPPORT_PHONE_DISPLAY}
      </p>
      <p className="text-sm font-medium leading-5 text-gray-500">
        Mon - Fri, 9am - 5pm EST
      </p>
    </div>
  );
}

function NeedHelpActions({ className }: { className?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <Button
        asChild
        className="h-12 w-full gap-2 border border-primary-500 px-5 text-sm font-semibold leading-5"
      >
        <a href={`tel:${SUPPORT_PHONE_TEL}`}>
          <PhoneCall01 size="20" className="size-5 shrink-0" />
          Call Us
        </a>
      </Button>
      <Button
        type="button"
        variant="secondary"
        className="h-12 w-full gap-2 bg-white px-5 text-sm font-semibold leading-5"
        onClick={() => {
          void navigator.clipboard.writeText(SUPPORT_PHONE_DISPLAY).then(
            () => setCopied(true),
            () => setCopied(false)
          );
        }}
      >
        <Copy06 size="20" className="size-5 shrink-0" />
        {copied ? 'Copied' : 'Copy Number'}
      </Button>
    </div>
  );
}
